import { indexBy } from 'volta/utils/array-utils';
import { chartDataFromDashboardKpis } from 'volta/utils/chart-utils';

import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type DashboardSummary from 'volta/models/dashboard-summary';
import type { IPlanningsKpis } from 'volta/models/types/stats';
import type BvMetricsService from 'volta/services/bv-metrics';
import { IDashboardWidget } from "volta/models/types/user";
const TIME_PERIOD = [
  { label: 'daysCount', period: 'day', count: 30 },
  { label: 'weeksCount', period: 'week', count: 24 },
  { label: 'monthsCount', period: 'month', count: 12 }
];

export interface IDashboardQueryParams {
  warehouse?: string;
  supplier?: string;
  deliveryWorkshop?: string;
  workshop?: string;
  acqCode?: string;
  tags?: string;
  planner?: string;
  timePeriod?: string;
  timePeriodCount?: string;
  tagsOperator?: string;
}

interface IArgs {
  planningsKpis?: IPlanningsKpis;
  planningsKpisLoading?: boolean;
  dashboardSummary?: DashboardSummary;
  dashboardSummaryLoading?: boolean;
  timePeriod: 'week' | 'day' | 'month';
  timePeriodCount: number;
  onTimePeriodChange?: (params: object) => void;
  onFilterPlannings?: (priority: string) => void;
  onFilterAlerts?: (priority: string, zoneType: string) => void;
  widget: IDashboardWidget;
}

export default class PlanningDashboard extends Component<IArgs> {
  // Services
  // ~~~~~

  @service bvMetrics!: BvMetricsService;

  // Properties
  // ~~~~~

  @tracked alertsDetailsCollapsed: boolean = true;
  @tracked allStatsExpanded: boolean = false;
  @tracked query?: IDashboardQueryParams;

  timePeriods = TIME_PERIOD;

  get widgetArgs() {
    return indexBy(
      Object.entries(this.args.widget.args ?? {}).map(([key, w]) => ({ ...w, name: key })),
      'name'
    );
  }

  // Computed Properties
  // ~~~~~

  /**
   * Dashboard stats summary data
   */

  get planningsKpis() {
    return this.args.planningsKpis;
  }

  get chartData() {
    return chartDataFromDashboardKpis(this.args.planningsKpis);
  }

  // Filters CPs

  get currentTimePeriod() {
    return this.timePeriods.find((period) => {
      return period.period === this.args.timePeriod;
    });
  }

  // Lifecycle Hooks
  // ~~~~~

  // Actions
  // ~~~~~

  @action
  showPlanningsByPriority(priority: string) {
    this.bvMetrics.trackAction(this, 'showPlanningsByPriority', priority);
    this.args.onFilterPlannings?.(priority);
  }

  @action
  showExecutionAlertsByPriority(zoneType: string, priority: string) {
    this.bvMetrics.trackAction(this, 'showExecutionAlertsByPriority', `${priority}-${zoneType}`);
    this.args.onFilterAlerts?.(priority, zoneType);
  }

  @action
  onTimePeriodChange(period: { period: string; count: number }) {
    this.bvMetrics.trackAction(this, 'onTimePeriodChange');
    const query = {
      timePeriod: period.period,
      timePeriodCount: period.count
    };

    this.args.onTimePeriodChange?.(query);
  }

  // Helper Function
  // ~~~~~

  // Private Callbacks
  // ~~~~~
}
